package io.github.jangalinski.pages._2015._02._28

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initDeploySpringbootAsWarPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("2015/02/28/DeploySpringbootAsWar.md", FrontMatterElement.Builder { addScalar("title", "Dual build springboot jar and war"); addScalar("date", "2015-02-28"); }))
}

@Page("/2015/02/28/deploy-springboot-as-war")
@Layout(".site.components.layout.BlogLayout")
@Composable
fun DeploySpringbootAsWarPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One of the great advantages of spring-boot applications is that they can run as single jar using an embedded web server or be deployed on an existing application server (or servlet container).")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This post describes how to configure a maven dual build that allows to create the executable jar and the deployable war file with one pom.xml set up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("First, define a property for the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<packaging>")
        }
        org.jetbrains.compose.web.dom.Text(" attribute (I use \"jar\" as default):")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code { org.jetbrains.compose.web.dom.Text("""<packaging>${'$'}{packaging.type}</packaging>
<properties>
    <packaging.type>jar</packaging.type>
</properties>
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now you could switch to a war packaging via property switch ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Ppackaging.type=war")
        }
        org.jetbrains.compose.web.dom.Text(". However, that would build a war file that includes the embedded tomcat used in the executable jar. To get rid of the superfluous dependencies, let's define a profile and overwrite the scope. As we already use a profile, we can use it to set the property as well:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code { org.jetbrains.compose.web.dom.Text("""<profiles>
    <profile>
        <id>war</id>
        <properties>
            <packaging.type>war</packaging.type>
        </properties>
        <dependencies>
            <dependency>
                <groupId>org.springframework.boot</groupId>
                <artifactId>spring-boot-starter-tomcat</artifactId>
                <scope>provided</scope>
            </dependency>
        </dependencies>
    </profile>
</profiles>
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now, when I run ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mvn -Pwar clean package")
        }
        org.jetbrains.compose.web.dom.Text(", I get a war file that I can deploy in any servlet-container, when I ommit the profile switch, I get a standalone, executable jar that includes an embedded tomcat.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nice. But thats not all. We need to configure our Spring Boot Application main class so it registers our controllers and views on deployment in the container.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is described in ")
        com.varabyte.kobweb.silk.components.navigation.Link("http://docs.spring.io/spring-boot/docs/current/reference/html/howto-traditional-deployment.html") {
            org.jetbrains.compose.web.dom.Text("howto-traditional-deployment")
        }
        org.jetbrains.compose.web.dom.Text(": Define a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SpringBootServletInitializer")
        }
        org.jetbrains.compose.web.dom.Text(" that adds your main class to the sources. I just let my Application extend the Initializer and overwrote the configure method:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code { org.jetbrains.compose.web.dom.Text("""@Override
protected SpringApplicationBuilder configure(final SpringApplicationBuilder builder) {
    return builder.sources(this.getClass());
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Of course, now I have an Initializer in my code even when I run the executable jar. It doesn't hurt much, but its more invasive then I want it to be. I could think of generating the Servlet-Initializer based on the chosen profile, since it only has to add one class to the sources. I'll keep you updated.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Check out the complete example on github: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/jangalinski/springboot-playground") {
            org.jetbrains.compose.web.dom.Text("springboot-playground")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
